// Frontend-computed KPI metrics: values derived in JS from OTHER already-fetched
// cube measures plus non-cube inputs (Financial Settings cost assumptions),
// rather than read from a single cube column. Today this powers the Amazon
// Contribution metrics, which combine Amazon revenue + real Amazon Ads spend
// (both from the `metrics` cube) with the company's cost-ratio assumptions.
//
// A computed metric key carries the `computed.` prefix so `sourceForMetricKey`
// routes it to its own row builder (`processComputedMetrics` in the KPI page's
// connect.ts) instead of the one-key-per-cube-column path. Catalog entries for
// these keys come from a `KPI_METRIC_GROUPS` group like any other metric; this
// registry holds only the per-period compute functions, keyed by the same key.

import type { MetricFormatType } from './types';

export const COMPUTED_METRIC_PREFIX = 'computed.';

export const AMAZON_CONTRIBUTION_KEY = 'computed.amazon_contribution';
export const AMAZON_CONTRIBUTION_MARGIN_KEY = 'computed.amazon_contribution_margin';

/**
 * Resolves whether a metric key is a frontend-computed metric. Mirrors the
 * prefix check `sourceForMetricKey` uses, exposed for callers that need it
 * without the full source classification.
 */
export function isComputedMetricKey(key: string): boolean {
  return key.startsWith(COMPUTED_METRIC_PREFIX);
}

// The Amazon cost-ratio assumptions from CompanyFinancials, all stored on a 0–1
// scale (e.g. 0.65 = 65%). Defaults mirror DEFAULT_VALUES in
// apps/ui-rtk/src/pages/Setup/FinancialSettingsPage/connect.ts. The shared lib
// can't import from the UI app, so the defaults are duplicated here; a spec
// guards the formula, and these are stable business constants.
export interface AmazonFinancials {
  amazonCogsPercent: number;
  amazonShippingCostPercent: number;
  amazonCcFeePercent: number;
  amazonDiscountPercent: number;
  amazonRefundRate: number;
  amazonAdSpendPercent: number;
}

export const AMAZON_FINANCIALS_DEFAULTS: AmazonFinancials = {
  amazonCogsPercent: 0.65,
  amazonShippingCostPercent: 0.15,
  amazonCcFeePercent: 0.15,
  amazonDiscountPercent: 0.05,
  amazonRefundRate: 0.05,
  amazonAdSpendPercent: 0.05,
};

/**
 * Fills a (possibly partial/null) CompanyFinancials-shaped object with the
 * Amazon cost ratios, falling back to defaults for any unset field.
 */
export function resolveAmazonFinancials(financials?: Partial<AmazonFinancials> | null): AmazonFinancials {
  return {
    amazonCogsPercent: financials?.amazonCogsPercent ?? AMAZON_FINANCIALS_DEFAULTS.amazonCogsPercent,
    amazonShippingCostPercent: financials?.amazonShippingCostPercent ?? AMAZON_FINANCIALS_DEFAULTS.amazonShippingCostPercent,
    amazonCcFeePercent: financials?.amazonCcFeePercent ?? AMAZON_FINANCIALS_DEFAULTS.amazonCcFeePercent,
    amazonDiscountPercent: financials?.amazonDiscountPercent ?? AMAZON_FINANCIALS_DEFAULTS.amazonDiscountPercent,
    amazonRefundRate: financials?.amazonRefundRate ?? AMAZON_FINANCIALS_DEFAULTS.amazonRefundRate,
    amazonAdSpendPercent: financials?.amazonAdSpendPercent ?? AMAZON_FINANCIALS_DEFAULTS.amazonAdSpendPercent,
  };
}

// Source cube measures a computed metric reads. Resolved per period bucket via
// the getter passed to `compute` (which projects either the current row or its
// `compared.` YoY twin onto these bare keys).
export const AMAZON_TOTAL_REVENUE_KEY = 'metrics.amazon_total_revenue';
export const AMAZON_ADS_TOTAL_SPEND_KEY = 'metrics.amazon_ads_total_spend';

/** Reads a source measure value for one period bucket; missing → 0. */
export type SourceGetter = (key: string) => number;

/**
 * Amazon Contribution ($) for one period:
 *   adCost    = max(real Amazon Ads spend, revenue × ad-spend assumption)
 *   otherCost = revenue × (COGS + shipping + CC/seller fees + discount + refund)
 *   result    = revenue − adCost − otherCost
 * The `max` implements "take whichever Amazon spend source is larger": when
 * Amazon Ads isn't connected real spend is 0 and the assumption wins; when real
 * spend exceeds the assumption, real wins. All ratios are % of total revenue.
 */
export function computeAmazonContribution(get: SourceGetter, settings: AmazonFinancials): number {
  const revenue = get(AMAZON_TOTAL_REVENUE_KEY);
  const realAdSpend = get(AMAZON_ADS_TOTAL_SPEND_KEY);
  const assumedAdSpend = revenue * settings.amazonAdSpendPercent;
  const adCost = Math.max(realAdSpend, assumedAdSpend);
  const otherCostRatio =
    settings.amazonCogsPercent +
    settings.amazonShippingCostPercent +
    settings.amazonCcFeePercent +
    settings.amazonDiscountPercent +
    settings.amazonRefundRate;
  const otherCost = revenue * otherCostRatio;
  return revenue - adCost - otherCost;
}

/**
 * Amazon Contribution Margin (%) = contribution / revenue × 100. Returns null
 * when revenue ≤ 0 to avoid a divide-by-zero. A null keeps the YoY change blank
 * and exports as an empty CSV cell (useKPIExport skips null/undefined); note the
 * KPITable 'percentage' renderer coalesces a null in-table cell to 0 and shows
 * "0.00%", the same as the existing cube-based contribution_margin_percent.
 * Pre-scaled to 0–100 because that renderer only appends '%', it does not multiply.
 */
export function computeAmazonContributionMargin(get: SourceGetter, settings: AmazonFinancials): number | null {
  const revenue = get(AMAZON_TOTAL_REVENUE_KEY);
  if (revenue <= 0) return null;
  return (computeAmazonContribution(get, settings) / revenue) * 100;
}

export interface ComputedMetricDef {
  key: string;
  label: string;
  type: MetricFormatType;
  suffix?: string;
  /** Source measure keys this metric reads — must already be fetched by the KPI query. */
  inputs: string[];
  /** Per-period value from the row's source measures + the company's cost assumptions. */
  compute: (get: SourceGetter, settings: AmazonFinancials) => number | null;
}

export const COMPUTED_METRICS: Record<string, ComputedMetricDef> = {
  [AMAZON_CONTRIBUTION_KEY]: {
    key: AMAZON_CONTRIBUTION_KEY,
    label: 'Amazon Contribution',
    type: 'currency',
    inputs: [AMAZON_TOTAL_REVENUE_KEY, AMAZON_ADS_TOTAL_SPEND_KEY],
    compute: computeAmazonContribution,
  },
  [AMAZON_CONTRIBUTION_MARGIN_KEY]: {
    key: AMAZON_CONTRIBUTION_MARGIN_KEY,
    // '%' suffix distinguishes this Financial-Settings-computed percentage
    // from the dbt-estimated dollar metric (metrics.amazon_contribution_margin).
    label: 'Amazon Contribution Margin %',
    // 'percentage' (not 'percent') — the KPITable renderer only formats
    // 'currency' | 'percentage' | 'decimal'; 'percentage' appends '%'. No suffix.
    type: 'percentage',
    inputs: [AMAZON_TOTAL_REVENUE_KEY, AMAZON_ADS_TOTAL_SPEND_KEY],
    compute: computeAmazonContributionMargin,
  },
};

export function getComputedMetric(key: string): ComputedMetricDef | undefined {
  return COMPUTED_METRICS[key];
}
